import { useCallback, useEffect, useRef, useState } from 'react'
import { BoxTruckLoadAnnotation, BoxTruckOperatorBid, type EffectivePickupWindow, LoadBid } from '@numeo/types'
import { CanonicalLoad, type CanonicalLoadInput } from '@numeo/types-core'
import { searchLoadsStreaming } from 'features/load-hub/api/loads.api'
import { isSearchRefusal } from 'features/load-hub/api/searchRequestFailed'
import { trackBoxTruckSearchOutcome } from '../utils/analytics'
import { sameLiveLoadExternalId, toCanonicalLoad } from 'features/load-hub/hooks/useLiveLoadSearch'
import { LoadSearchRequest } from 'features/load-hub/types/api'
import { loadKey } from '../utils/loadFields'
import {
    BoxTruckSearchFilters,
    BEON_SOURCE,
    boxTruckPickupWindow,
    buildRequests,
    DAT_SOURCE,
    boardSearchInstanceId,
    matchesEquipmentLabels,
    PAGE_SIZE,
    searchWouldCarrySource,
    SOURCE,
} from './requestMapping'
import { BoxTruckEmailThread, useBoxTruckOverlays } from './useBoxTruckOverlays'
import { useBoxTruckLiveSearch } from './useBoxTruckLiveSearch'
import { LiveSearchLifetime } from './useLiveSearchRenewal'
import { showError } from '@/utils/snackbar'

/** Generous safety ceiling on the LIVE-prepend (WebSocket) path only. Pagination stops on its own once every
 *  feed returns a short page, but a board left open for hours with auto-refresh streams live loads into an
 *  ever-growing array + unvirtualized DOM; cap the accumulation so a long-lived board can't degrade. Far
 *  above any real single-lane feed, so it never trims what pagination legitimately loaded. */
const MAX_BOARD_LOADS = 2000

/**
 * Put newly-arrived loads at the top of the board, deduped by `loadKey`, capped.
 *
 * Shared by the board's TWO live paths — a server-matched live-search delivery and the operator broadcast —
 * because they must not order the board differently. A re-posted load moves back to the top rather than
 * appearing twice, and the cap bounds an accumulation that pagination does not: a board left open for hours
 * streams live loads into an ever-growing array and an unvirtualized DOM. Far above any real single-lane
 * feed, so it never trims what pagination legitimately loaded.
 */
const mergeLiveLoads = (previous: CanonicalLoad[], incoming: CanonicalLoad[]): CanonicalLoad[] => {
    const incomingKeys = new Set(incoming.map(loadKey))
    return [...incoming, ...previous.filter((load) => !incomingKeys.has(loadKey(load)))].slice(0, MAX_BOARD_LOADS)
}

/** How many per-driver lane requests may stream at once. Chrome allows 6 connections per origin, and the
 *  board shares that budget with the overlay fetches and the rest of the app — so a pool of 3 leaves room
 *  rather than saturating it. Picking more drivers takes longer, never more simultaneous connections. */
const MAX_CONCURRENT_LANES = 3

/** The next-page cursor of each lane, by its place in the search's lane list; null (or absent) once that lane has no more. */
type LaneCursors = Record<number, string | null>

/** A tab's frozen board — everything needed to restore it instantly on switch-back (loads + pagination
 * cursors), so a searched tab never re-streams just because the operator toggled away and back. Mirrors
 * Load Hub's per-tab `tabLoads`, but also carries the box-truck feed's cursors. */
interface BoardSnapshot {
    loads: CanonicalLoad[]
    hasMore: boolean
    lastUpdatedAt: Date | null
    offset: number
    /** Which feeds reported a FULL page on the last fetch — the per-feed "keep paginating" gate. */
    feedsWithMore: Record<string, boolean>
    cursors: LaneCursors | null
    filters: BoxTruckSearchFilters
    /** The pickup range the tab's QUERY ran on. Snapshotted with the filters because it is clamped against a
     *  clock: re-resolving it on switch-back would re-clamp at a later midnight and move the restored board's
     *  live scope onto a day its loads never came from. */
    pickupWindow: EffectivePickupWindow | null
}

interface UseBoxTruckLoadsResult {
    loads: CanonicalLoad[]
    loading: boolean
    error: string | null
    lastUpdatedAt: Date | null
    /** Driver/carrier bids on the visible loads, keyed by raw externalId (loadKey). */
    bids: Record<string, LoadBid[]>
    /** Successful bid emails sent by operators, keyed by load id. */
    operatorBids: Record<string, BoxTruckOperatorBid[]>
    /** The team's shared star rating + note per load, keyed by raw externalId (one per load). */
    annotations: Record<string, BoxTruckLoadAnnotation>
    /** Broker email-thread state per load (thread id + unread replies), keyed by raw externalId. */
    emailThreads: Record<string, BoxTruckEmailThread>
    /** The loads the thread overlay has really answered for — see `useBoxTruckOverlays`. */
    threadsKnownFor: ReadonlySet<string>
    /** Set the team's rating + note on a load (optimistic; teammates get it over WS). `kind` picks the
     *  success toast copy ("Rating saved" / "Note saved"). */
    setAnnotation: (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => Promise<void>
    /** Re-run from the top: Sylectus always, plus live DAT when origin or destination narrows the lane. */
    search: (filters: BoxTruckSearchFilters) => void
    /** Reset to the empty "New search" state (switching to a not-yet-searched tab) — no request fired. */
    clear: () => void
    /** Quiet re-fetch of the ACTIVE search (page 1) without blanking the board — auto-refresh / Refresh Feed. */
    refresh: () => void
    /** Fetch the next Sylectus page and append it (infinite scroll). No-op while a fetch is in flight. */
    loadMore: () => void
    /** Another Sylectus page is available beyond what's loaded. */
    hasMore: boolean
    /** A load-more (append) fetch is in flight — distinct from the initial-search skeleton `loading`. */
    loadingMore: boolean
    /** Freeze the ACTIVE tab's board under `tabId` (called on switch-away) so returning to it is instant. */
    saveSnapshot: (tabId: string) => void
    /** Restore a tab's frozen board; returns false when nothing was cached (caller should run a fresh search). */
    restoreSnapshot: (tabId: string) => boolean
    /** Forget a tab's cached board. Does NOT touch its live subscriptions — see `releaseBoard`. */
    dropSnapshot: (tabId: string) => void
    /** Release a CLOSED tab's live subscriptions. Call this from close paths, not from cache eviction. */
    releaseBoard: (tabId: string) => void
    /**
     * Per board instance id, the lifetime the server granted that board's live subscriptions — what
     * `useLiveSearchRenewal` needs to keep them from lapsing while the tab stays open.
     *
     * Absent for a board that has no live search at all: one that was never searched, or an operator the
     * surface gate excludes (no `live_search_ready` ever arrives, so nothing is recorded and nothing is
     * renewed).
     */
    liveSearchLifetimes: Record<string, LiveSearchLifetime>
    /** A gated Sources chip was switched OFF — drop that feed's stale "keep paginating" flag. */
    forgetGatedSource: (source: string) => void
    /** A gated Sources chip was switched back ON — merge that feed into the board without rebuilding it. */
    revealGatedSource: (source: string) => void
}

/**
 * Owns the Box Truck Loads feed from loads-ingest-service unified search, deduped by externalId. Sylectus runs
 * on every search; live DAT joins only after origin or destination narrows the lane, so anywhere-to-anywhere
 * remains a DB-backed Sylectus query. Pickup and equipment further narrow it when set. Fetches on search();
 * cancels the in-flight stream on unmount and whenever a new fetch starts.
 */
export const useBoxTruckLoads = (
    currentOperatorId?: string,
    /**
     * Request SOURCE NAMES to leave out of every search — the gated Sources-bar chips the operator switched
     * off, already translated from chip keys (`gatedSourcesFor`). Names, not chip keys: this is compared
     * against `sources`, and the two vocabularies only coincide for DAT.
     *
     * A ref so a request reads the live value rather than the one captured when the callback was built, and
     * mutated by the toggle handler BEFORE it asks for a refetch: switching a source back on has to fetch it,
     * and reading this through render state would re-run the search with the value from before the click —
     * excluding the very source the operator just asked for.
     */
    excludedSourcesRef?: { readonly current: ReadonlySet<string> },
    /**
     * The ACTIVE tab's live-search identity, written by the page. A ref for the same reason as the set above:
     * the tab can change between building this callback and running a search, and a subscription registered
     * under the previous tab's id would be replaced out from under the tab that still holds it.
     *
     * Null (or omitted) asks for no subscription, so the board falls back to the operator broadcast alone.
     */
    liveSearchBoardIdRef?: { readonly current: string | null },
    /**
     * Whether this operator's searches go over the search stream (load-search-service) instead of `search-unified` — the
     * A/B arm `featureFlags.boxTruckStreamSearch` names, written by the page. A ref for the same reason as the two above:
     * a flag that arrives mid-session reaches the next request without changing the identity of the callbacks built here.
     * Omitted means `search-unified`.
     */
    viaSearchStreamRef?: { readonly current: boolean }
): UseBoxTruckLoadsResult => {
    const [loads, setLoads] = useState<CanonicalLoad[]>([])
    // Starts NOT loading: the board no longer auto-runs an (expensive anywhere) search on mount. A search
    // fires only when a tab is explicitly searched — the page shows a "New search" empty state until then.
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    // The granted lifetime per board, so the renewal timer can run off the server's own expiry instead of a
    // second copy of its TTL. State rather than a ref: a timer has to re-arm when a new one arrives.
    const [liveSearchLifetimes, setLiveSearchLifetimes] = useState<Record<string, LiveSearchLifetime>>({})
    // Per-load overlays (bids, operator bid-emails, team rating/note) + their live WS upserts live in their
    // own hook; this hook just refetches them for each new page via fetchOverlaysFor.
    const { bids, operatorBids, annotations, emailThreads, threadsKnownFor, setAnnotation, fetchOverlaysFor } = useBoxTruckOverlays(currentOperatorId)
    const abortRef = useRef<AbortController | null>(null)
    // A load-more (append) fetch has its own controller so a fresh search can abort it without also
    // aborting itself, and vice-versa.
    const loadMoreAbortRef = useRef<AbortController | null>(null)
    // A background merge (revealing a switched-on source) has a third slot, so it can never cancel a page
    // whose cursor has already advanced.
    const mergeAbortRef = useRef<AbortController | null>(null)
    // The filters behind the currently-shown results — so the live WS feed only prepends loads that
    // match the active lane search (a GA→FL load shouldn't jump into an "origin=TX" result set).
    //
    // Null while NO search is on screen: before the first one, and after `clear()` puts an unsearched tab's
    // "New search" state up. Not EMPTY_FILTERS — those are a real query, anywhere-to-anywhere, and every
    // live gate below reads them as "match everything". Holding them on an unsearched tab let each Sylectus
    // post the socket delivered pile up behind the hero, where the Sources chip counted rows nobody had
    // asked for and the table never showed. Null makes every path that continues a search (live rows,
    // refresh, load-more, a source reveal) a no-op until one exists.
    const activeFiltersRef = useRef<BoxTruckSearchFilters | null>(null)
    /** The range the CURRENT board was searched on — resolved once per search and held until the next one.
     *  Past days clamp forward to today, so resolving it per live event would silently re-clamp at midnight
     *  and judge the feed by a day the query never ran for. */
    const activePickupWindowRef = useRef<EffectivePickupWindow | null>(null)
    // Offset of the last-fetched page, and which feeds still have more to give — together they drive the next
    // loadMore offset and the hasMore gate. `loadsRef` mirrors `loads` so an append can seed its dedup map
    // synchronously.
    const offsetRef = useRef(0)
    // Per-feed "the server returned a FULL page, so keep paginating this leg", keyed by source. The server
    // reports it from the raw DB row count, BEFORE app-side deadhead/freshness trimming — so a page that loses
    // a few rows on the way out still correctly reads as "more remain". Live DAT has no real pagination and
    // reports nothing, so its leg naturally drops after page 1; the DB feeds keep going until a short page.
    //
    // This replaced a per-feed grand total from COUNT(*) OVER(): that window scanned the entire matched set
    // (31 ms -> 48.7 s on a prod box-truck page 1) purely to feed this gate, since no UI rendered the number.
    const feedsWithMoreRef = useRef<Record<string, boolean>>({})
    // Where each lane's next page starts when the search stream is the one paging (it takes a cursor, not an offset);
    // null while `search-unified` pages by `offsetRef`.
    const cursorsRef = useRef<LaneCursors | null>(null)
    const loadsRef = useRef<CanonicalLoad[]>([])
    // Ref mirrors of the reactive board state, so a tab snapshot can be captured synchronously (the state
    // setters below run async, but saveSnapshot must read the CURRENT board the instant the tab switches away).
    const hasMoreRef = useRef(false)
    const lastUpdatedAtRef = useRef<Date | null>(null)
    // Per-tab board cache (Load Hub parity): each searched tab's last board is frozen here on switch-away and
    // thawed on switch-back, so toggling between tabs is instant instead of re-streaming every time.
    const cacheRef = useRef<Record<string, BoardSnapshot>>({})
    // Live deliveries have no DB id of their own. Negative and descending, exactly as Load Hub allocates
    // them, so a live row can never collide with a fetched one on the table's key.
    const syntheticIdRef = useRef(-1_000_000)

    /**
     * Apply one server-matched live delivery to the board.
     *
     * Unlike the broadcast path below, these postings were matched by the server against the very criteria
     * this search ran on, so they are NOT re-tested against the client's mirror of the lane, date, capacity
     * and source filters — re-testing is what a fail-open mirror exists to avoid getting wrong. Equipment is
     * the exception and is checked, because it is the one dimension the server's live matcher does not
     * evaluate at this board's resolution (see `matchesEquipmentLabels`).
     */
    /**
     * Which cached tab a delivery belongs to, when it is not the one on screen.
     *
     * Composed forward — `boardSearchInstanceId(tabId)` — never by taking the id apart. The board id's shape
     * has one owner, and parsing it back here is the mistake that silently breaks routing when that shape
     * changes.
     */
    const snapshotTabFor = (boardInstanceId: string): string | undefined =>
        Object.keys(cacheRef.current).find((tabId) => boardSearchInstanceId(tabId) === boardInstanceId)

    const liveSearch = useBoxTruckLiveSearch({
        onLoadCreated: (boardInstanceId: string, incoming: CanonicalLoadInput, source: string) => {
            // Projected by Load Hub's own converter — these are the same socket payloads, and a second
            // projection is a second place for the id/`isActive`/timestamp defaults to drift.
            const load = toCanonicalLoad(incoming, syntheticIdRef.current--, source)
            if (boardInstanceId === liveSearchBoardIdRef?.current) {
                const active = activeFiltersRef.current
                if (!active || !matchesEquipmentLabels(load, active)) return
                setLoads((prev) => mergeLiveLoads(prev, [load]))
                return
            }
            // A tab that is not on screen still asked for this load, so it goes into the board that tab is
            // FROZEN as — which is what `restoreSnapshot` thaws on switch-back, and it does not re-search.
            // Dropping it instead left that tab missing exactly the rows that arrived while it was away.
            // Judged against that tab's own equipment filter, not the active tab's.
            const tabId = snapshotTabFor(boardInstanceId)
            const snapshot = tabId ? cacheRef.current[tabId] : undefined
            if (!snapshot || !matchesEquipmentLabels(load, snapshot.filters)) return
            snapshot.loads = mergeLiveLoads(snapshot.loads, [load])
        },
        onLoadCancelled: (boardInstanceId: string, externalLoadId: string, source: string) => {
            // Dropped, which is what the next refresh would do anyway — a covered or superseded posting
            // is not returned by the search. This is only that, sooner: until now the board had no way to
            // learn a posting had gone and kept offering it until someone refreshed.
            const gone = (rows: CanonicalLoad[]) => rows.filter((l) => !sameLiveLoadExternalId(l, externalLoadId, source))
            if (boardInstanceId === liveSearchBoardIdRef?.current) {
                setLoads((prev) => {
                    const next = gone(prev)
                    return next.length === prev.length ? prev : next
                })
                return
            }
            const tabId = snapshotTabFor(boardInstanceId)
            const snapshot = tabId ? cacheRef.current[tabId] : undefined
            if (snapshot) snapshot.loads = gone(snapshot.loads)
        },
    })

    // Fetch the Sylectus page at `offset`. `append=false` is a fresh search (page-1 supplement included,
    // board replaced); `append=true` is a load-more that merges the next Sylectus page onto what's loaded.
    // `quiet` (fresh only) keeps the current board on screen and swaps it atomically when page 1 arrives —
    // for auto-refresh / Refresh Feed, so the feed doesn't blank on every tick.
    const runSearch = useCallback(
        (filters: BoxTruckSearchFilters, offset: number, append: boolean, quiet = false, backgroundMerge?: string) => {
            // A BACKGROUND MERGE is additive but is not a page: it adds a feed to the board that is already
            // there. So it gets its own abort slot, leaves the pagination cursor alone, shows no load-more
            // spinner, and reports nothing about pagination. Sharing the load-more slot would have it CANCEL
            // an in-flight page whose cursor had already advanced — losing those rows with nothing to refetch
            // them, since the cursor keeps pointing past the hole.
            if (backgroundMerge) mergeAbortRef.current?.abort()
            else if (append) loadMoreAbortRef.current?.abort()
            else {
                abortRef.current?.abort()
                loadMoreAbortRef.current?.abort()
                mergeAbortRef.current?.abort()
            }
            const controller = new AbortController()
            if (backgroundMerge) mergeAbortRef.current = controller
            else if (append) loadMoreAbortRef.current = controller
            else abortRef.current = controller
            activeFiltersRef.current = filters
            // Resolved HERE, once, and passed to the request builder so the query and the live gate can never
            // be looking at two different ranges.
            //
            // An APPEND continues the query already on screen, so it pages through the range that query ran
            // on. Resolving a new one here would re-clamp against a later clock — a board paginated across
            // local midnight would fetch its next page for a different day while keeping the offset and the
            // rows from the old one. Only a fresh search or a refresh (both `append: false`) picks a new range.
            const pickupWindow = append ? activePickupWindowRef.current : boxTruckPickupWindow(filters)
            if (!append) activePickupWindowRef.current = pickupWindow
            if (!backgroundMerge) offsetRef.current = offset

            // Three cases, spelled out rather than implied by a compound condition — the same shape
            // `finishPage` uses to clear them. Folded into `append && !backgroundMerge`, a background merge
            // fell through to the FRESH-SEARCH branch and claimed `loading`, which `finishPage` then
            // deliberately never cleared: infinite scroll dead for the rest of the session, the board stuck
            // loading, auto-refresh greyed out, and a live error banner wiped on the way in.
            if (backgroundMerge) {
                // No spinner at all: additive, and not a page. Nothing to clear later either.
            } else if (append) setLoadingMore(true)
            else {
                setLoading(true)
                setError(null)
                if (!quiet) {
                    // Fresh search: clear the board immediately so it shows the loader in place of the stale rows
                    // until the FIRST new result streams in — then the table renders and the rest (Sylectus, then
                    // the DAT supplement) stream on top. A QUIET refresh skips this: the current board stays up and
                    // is replaced atomically when page 1 lands (the totals below are overwritten on that page too).
                    setLoads([])
                    loadsRef.current = []
                    // Reset the supplement totals so a prior search's larger counts don't keep the load-more gate
                    // open before this search's supplement counts arrive.
                    feedsWithMoreRef.current = {}
                }
            }

            // The rows THIS run produced, and nothing else.
            const byKey = new Map<string, CanonicalLoad>()
            /**
             * Appending composes onto whatever is on the board AT COMMIT TIME; a fresh search or a quiet
             * refresh REPLACES it with this run's rows — so a load that went inactive (booked/expired) between
             * ticks is pruned, not left lingering with a stale price/status.
             *
             * Read live, not captured at the top. A background merge and a load-more now hold separate abort
             * slots, so they can be in flight together — and a map seeded when the run STARTED would commit a
             * board that predates whatever the other one added, silently dropping it. Whichever landed second
             * used to win outright: reveal DAT mid-scroll and the page arrives last, taking the revealed feed
             * off a board whose chip still reads on. Composing at commit keeps both.
             */
            const commit = (): CanonicalLoad[] => {
                const merged = append ? new Map<string, CanonicalLoad>(loadsRef.current.map((l) => [loadKey(l), l])) : new Map<string, CanonicalLoad>()
                // This run's rows win on a duplicate key (the fresher payload) but keep the board's existing
                // position for that load — `Map.set` on a present key does not reorder.
                for (const [key, load] of byKey) merged.set(key, load)
                const result = Array.from(merged.values())
                loadsRef.current = result
                setLoads(result)
                return result
            }

            // Include the DAT supplement while ITS last page came back full (always on page 1, before anything
            // is known). Live DAT reports no page-full signal, so in practice it drops after page 1.
            //
            // A chip the operator switched off drops out here, so the source is never ASKED for rather than
            // fetched and hidden — a live DAT search is an external call on a shared token pool, and paying
            // for one nobody is looking at is the whole cost this removes.
            const wantSupplement = (src: string) => !excludedSourcesRef?.current.has(src) && (offset === 0 || feedsWithMoreRef.current[src] === true)
            // Whether the stream reached all_complete. A stream can still abort AFTER delivering everything (the
            // shared 60s client timeout fires on an SSE connection the server kept open) — that trailing abort
            // must not be surfaced as a board error, so we track completion to swallow it in the catch below.
            // One search per pinned driver location (usually just one). Each lane streams into the SAME
            // dedup map, so the board is the union of what every picked truck can reach; the page-level
            // bookkeeping below runs once, when the last lane finishes.
            // A subscription describes the search the board is SHOWING, so only a run that rebuilds the board
            // asks for one. A load-more is a deeper page of a search that already has its subscription, and a
            // background merge adds one feed to that same board — either one registering again would replace
            // the live lane with a narrower description of it.
            const boardInstanceId = append || backgroundMerge ? undefined : (liveSearchBoardIdRef?.current ?? undefined)
            const requests = buildRequests(
                filters,
                offset,
                { dat: wantSupplement(DAT_SOURCE), beon: wantSupplement(BEON_SOURCE) },
                pickupWindow,
                boardInstanceId
            )
            const laneInstanceIds = requests.flatMap((request) => (request.liveSearch ? [request.liveSearch.clientSearchInstanceId] : []))
            if (boardInstanceId) liveSearch.beginSearch(boardInstanceId, laneInstanceIds)
            const runFeedsHasMore: Record<string, boolean> = {}
            // The search stream pages by cursor: what each lane returned this run, and how the run was paged. A page
            // continues from the cursors the previous one left; `null` here means offset paging, which needs none.
            const laneCursors: LaneCursors = {}
            let pagedBy: 'cursor' | 'offset' = 'offset'
            const pageFrom = append && !backgroundMerge ? cursorsRef.current : null
            let succeeded = 0
            // Whether any lane was turned away with a 4xx (quota, access), as against failing: the A/B tells the two apart.
            let refused = false
            // Read once: the arm a search ran on must not change under it if the flag lands mid-search. The arm is the
            // user's assignment, so a build with no stream host still counts as the arm it was meant to measure.
            const viaStream = viaSearchStreamRef?.current === true
            // Did the feed this reveal was for fail, and did it deliver anything? Both are tracked for THAT
            // source alone — a merge asks for Sylectus alongside it on every lane, so "the run produced rows"
            // says nothing about whether the revealed feed did. Reported once, at the end; see finishPage.
            let revealErrored = false
            let revealDelivered = false
            const finishPage = () => {
                if (controller.signal.aborted) return
                // With every lane failed there is nothing to show and nothing to clear — the catch below owns
                // the message in that case.
                if (succeeded > 0) setError(null)
                // One per Search the operator pressed: not a page, a quiet refresh or a reveal.
                if (!append && !quiet && !backgroundMerge) trackBoxTruckSearchOutcome(viaStream, succeeded > 0 ? 'success' : refused ? 'refused' : 'error')
                const merged = commit()
                setLastUpdatedAt(new Date())
                // The operator asked for this feed by clicking, so a failure cannot be silent — reported ONCE
                // here rather than per lane, and only when the run brought back nothing. A stream can error
                // AFTER delivering (the 60s client timeout on a connection the server left open), and telling
                // someone a feed failed while its rows are on screen is worse than saying nothing: the advice
                // to switch it off and on would throw away a feed that arrived fine.
                //
                // A toast and not `setError`, because `error` REPLACES the table — blanking a healthy board
                // over a supplement that did not arrive would be a worse answer than the one it reports.
                if (backgroundMerge && revealErrored && !revealDelivered) {
                    showError(`Couldn't load ${backgroundMerge} — switch it off and on to try again`)
                }
                if (backgroundMerge) {
                    // Nothing to clear: a background merge never claimed either spinner.
                } else if (append) setLoadingMore(false)
                else setLoading(false)
                // More remains while ANY feed on ANY lane returned a full page — so scrolling walks the whole
                // board (Sylectus AND every supplement, around every picked driver) to the bottom. Capped at
                // MAX_BOARD_LOADS so the accumulated array (and the virtualized row set) stays bounded even on
                // a huge nationwide feed; a narrower lane/equipment filter brings an over-cap board into range.
                // A background merge re-asks at offset 0 to pick up one feed; what page 1 reports says nothing
                // about the TAIL the board is sitting on. Writing it would resurrect `hasMore` on a board that
                // had already reached the end, and send the scroller past it for an empty page.
                if (!backgroundMerge) {
                    Object.assign(feedsWithMoreRef.current, runFeedsHasMore)
                    // A lane that failed keeps the cursor it had, so the next page still starts where this one should have.
                    cursorsRef.current = pagedBy === 'cursor' ? { ...(append ? pageFrom : null), ...laneCursors } : null
                }
                const anyFeedHasMore = Object.values(feedsWithMoreRef.current).some(Boolean)
                setHasMore(anyFeedHasMore && merged.length < MAX_BOARD_LOADS)
                // Refetch the per-load overlays (bids, operator bid-emails, team rating/note) for this page.
                // Only the rows THIS run produced — an append's earlier pages keep the overlays they were
                // fetched with, so the request no longer grows with the board (by page five it carried 750
                // ids four times over, of which the server read the first 500 and dropped the rest without
                // a word). A fresh search or a quiet refresh rebuilt the board, so it replaces the maps.
                fetchOverlaysFor(append ? [...byKey.values()] : merged, controller.signal, append ? 'merge' : 'replace')
            }
            const runLane = (request: LoadSearchRequest, lane: number): Promise<void> =>
                searchLoadsStreaming(
                    request,
                    {
                        signal: controller.signal,
                        onSourceLoads: (source, incoming) => {
                            if (controller.signal.aborted) return
                            // Rows for the revealed feed are what decide whether the reveal worked — not rows in
                            // general. A stream can also error AFTER delivering (the 60s client timeout), and a
                            // feed that arrived must not be reported as failed.
                            if (source === backgroundMerge && incoming.length > 0) revealDelivered = true
                            // Two legs, and the unified endpoint strips SOURCE| prefixes, so the same physical load can
                            // still arrive under the same bare externalId. Last write wins now that both legs are live
                            // feeds of equal freshness — the lagging DB copy that needed protecting here left with
                            // NUMEO_SEARCH.
                            for (const load of incoming) byKey.set(loadKey(load), load)
                            commit()
                        },
                        // Fires for EVERY completed source, including one whose visible batch was empty — a full DB
                        // page can filter down to zero rows app-side, and both signals below must survive that.
                        onSourceComplete: (source, meta) => {
                            if (controller.signal.aborted) return
                            // Record whether THIS feed's page came back full, on every page — the signal is per-page by
                            // nature and is what decides if this leg is fetched again. A feed that reports nothing (live
                            // DAT, a cache replay) reads as exhausted, which is exactly right: neither can serve a
                            // deeper offset.
                            // OR across lanes: one driver's feed still having a page means the board has more to show.
                            runFeedsHasMore[source] = (runFeedsHasMore[source] ?? false) || meta.hasMore === true
                        },
                        onSourceError: (source, message) => {
                            if (controller.signal.aborted) return
                            // Only the PRIMARY Sylectus feed failing is worth surfacing. The live-DAT supplement is
                            // best-effort; log it but keep the board rendering Sylectus-only rather than blanking it.
                            if (source !== SOURCE) {
                                // A REVEAL is an explicit click, so its failure has to be said out loud — but not
                                // from here: this fires per LANE, and one press with a fleet pinned would stack a
                                // toast per driver. Recorded, and reported once when every lane has settled.
                                if (backgroundMerge === source) revealErrored = true
                                console.warn(`[box-truck] supplementary source ${source} error (board keeps Sylectus-only):`, message)
                                return
                            }
                            // Surface a Sylectus failure only when there's nothing on the board — if the supplement
                            // already delivered rows, keep them rather than blanking the board on the primary's error.
                            if (!append && loadsRef.current.length === 0) setError(message || 'Failed to load the Sylectus feed')
                        },
                        onLiveSearchReady: ({ subscriptionId, expiresAt }) => {
                            const laneInstanceId = request.liveSearch?.clientSearchInstanceId
                            if (!boardInstanceId || !laneInstanceId) return
                            liveSearch.activate(boardInstanceId, laneInstanceId, subscriptionId)
                            // Recorded so the board can renew before this runs out — without it a tab left open
                            // simply stopped receiving deliveries at the TTL. Last lane wins rather than the
                            // earliest expiry: one search's lanes are registered within seconds of each other, so
                            // the difference is seconds against a renewal margin of minutes, and tracking the
                            // minimum would need a per-run marker here to stop a previous run's earlier expiry
                            // pinning the board forever.
                            const expiry = Date.parse(expiresAt)
                            if (!Number.isFinite(expiry)) return
                            setLiveSearchLifetimes((prev) => ({ ...prev, [boardInstanceId]: { expiresAt: expiry, receivedAt: Date.now() } }))
                        },
                        onComplete: (summary) => {
                            succeeded += 1
                            pagedBy = summary.pagedBy
                            laneCursors[lane] = summary.nextCursor
                        },
                    },
                    { viaSearchStream: viaStream }
                ).catch((err: unknown) => {
                    if (controller.signal.aborted) return
                    if (isSearchRefusal(err)) refused = true
                    if (backgroundMerge) {
                        revealErrored = true
                        return
                    }
                    if (append) return
                    // Never blank a board that already has data. A stream can error AFTER delivering results (the 60s
                    // client timeout aborting an SSE connection the server left open — "BodyStreamBuffer was aborted"),
                    // or drop mid-stream once some rows arrived; in both cases keep the rows on screen. Only surface the
                    // error when EVERY lane failed with nothing to show, so it doesn't nuke visible loads.
                    if (succeeded > 0 || loadsRef.current.length > 0) return
                    setError(err instanceof Error ? err.message : 'Failed to load the Sylectus feed')
                })

            // Lanes run a few at a time, NOT all at once. One press of Search with a large fleet picked would
            // otherwise open a streaming connection per driver simultaneously: past the browser's per-origin
            // connection limit the extra ones silently queue (so the board looks stalled rather than
            // streaming), and the search backend takes the whole fleet's load in one burst. A small pool keeps
            // the first lanes rendering immediately — which is the point of the per-driver split — while the
            // rest follow as slots free up. A lane never rejects (its own catch owns the error), so one bad
            // lane cannot stall the pool.
            const queue = requests.map((request, lane) => ({ request, lane }))
            const worker = async (): Promise<void> => {
                while (!controller.signal.aborted) {
                    const next = queue.shift()
                    if (!next) return
                    // Paged by cursor, a lane with none left has nothing more to ask: without one it would be given page 1 again.
                    const cursor = pageFrom?.[next.lane]
                    if (pageFrom && !cursor) continue
                    await runLane(cursor ? { ...next.request, cursor } : next.request, next.lane)
                }
            }
            void Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_LANES, requests.length) }, worker)).then(finishPage)
            // Everything else is read through refs; fetchOverlaysFor is a stable useCallback from useBoxTruckOverlays.
        },
        [fetchOverlaysFor, excludedSourcesRef, liveSearchBoardIdRef, viaSearchStreamRef, liveSearch]
    )

    // Public: a fresh search always restarts from the top (offset 0, supplement included).
    const search = useCallback((filters: BoxTruckSearchFilters) => runSearch(filters, 0, false), [runSearch])
    // Quiet refresh: re-run the ACTIVE search from page 1 without blanking the board (auto-refresh / Refresh
    // Feed). Re-fetches offset 0 for the last-searched filters; the board swaps to the fresh page-1 in place.
    const refresh = useCallback(() => {
        if (activeFiltersRef.current) runSearch(activeFiltersRef.current, 0, false, true)
    }, [runSearch])
    /**
     * A gated source was switched OFF: forget its pagination flag.
     *
     * `feedsWithMore` is only ever corrected by a run that ASKED for the feed, and a gated source is no longer
     * asked for — so a `true` left behind by the last run that did would never be cleared. `hasMore` is the OR
     * across that record, so it would stay true on an exhausted board and the infinite scroller would keep
     * firing `loadMore` into empty Sylectus pages.
     */
    const forgetGatedSource = useCallback((source: string) => {
        if (!(source in feedsWithMoreRef.current)) return
        delete feedsWithMoreRef.current[source]
        setHasMore(Object.values(feedsWithMoreRef.current).some(Boolean) && loadsRef.current.length < MAX_BOARD_LOADS)
    }, [])

    /**
     * A gated source was switched back ON: fetch it into the board that is already there.
     *
     * An APPEND, not a refresh. `refresh` re-runs with `append: false`, which seeds the dedup map empty and
     * replaces the board with page 1 — an operator who had scrolled to six hundred rows would watch them
     * collapse. This merges instead, and holds the pagination cursor where it was, so revealing a source adds
     * rows and changes nothing else. Offset 0 because that is the only offset a supplement is ever asked at.
     *
     * No-op when the active search could not carry the source anyway (an anywhere-to-anywhere board never
     * reaches DAT) — there is nothing to fetch, and a full request for a feed that cannot appear is waste.
     *
     * COST, stated plainly: this is a whole search per lane, not a request for the one feed. `sources` always
     * carries SYLECTUS_EMAIL (it is what marks a box-truck request server-side), and the builder has no way to
     * ask for a supplement alone — so with a fleet pinned, revealing DAT re-pulls a page of Sylectus rows the
     * board already holds and dedups them straight back onto themselves. It partly offsets what the gate
     * saves, and narrowing it needs a builder that can express "this feed only".
     */
    const revealGatedSource = useCallback(
        (source: string) => {
            const active = activeFiltersRef.current
            if (!active || !searchWouldCarrySource(active, source)) return
            runSearch(active, 0, /* append */ true, /* quiet */ true, /* backgroundMerge */ source)
        },
        [runSearch]
    )

    // Infinite scroll: fetch the next Sylectus page and append it. Guarded so overlapping scroll events
    // (or an in-flight initial search) never double-fetch the same page.
    const loadMore = useCallback(() => {
        if (loadingMore || loading || !hasMore || !activeFiltersRef.current) return
        runSearch(activeFiltersRef.current, offsetRef.current + PAGE_SIZE, true)
    }, [loadingMore, loading, hasMore, runSearch])

    // Reset the board to the empty "New search" state — used when switching to a tab that hasn't searched yet.
    // Aborts any in-flight stream and drops the current page/overlays without firing a request.
    const clear = useCallback(() => {
        abortRef.current?.abort()
        loadMoreAbortRef.current?.abort()
        mergeAbortRef.current?.abort()
        activeFiltersRef.current = null
        activePickupWindowRef.current = null
        offsetRef.current = 0
        cursorsRef.current = null
        loadsRef.current = []
        setLoads([])
        setError(null)
        setHasMore(false)
        setLoadingMore(false)
        setLoading(false)
    }, [])

    // Keep the ref mirrors in lockstep with their state, so saveSnapshot reads a consistent board.
    useEffect(() => {
        hasMoreRef.current = hasMore
    }, [hasMore])
    useEffect(() => {
        lastUpdatedAtRef.current = lastUpdatedAt
    }, [lastUpdatedAt])

    // Freeze the current board under a tab id (the page calls this for the OUTGOING tab on every switch-away).
    const saveSnapshot = useCallback((tabId: string) => {
        // An unsearched tab has no board to freeze. Caching one anyway handed the live broadcast below an
        // anywhere-to-anywhere board to fill, for every tab the operator merely passed through.
        if (!activeFiltersRef.current) {
            delete cacheRef.current[tabId]
            return
        }
        cacheRef.current[tabId] = {
            loads: loadsRef.current,
            hasMore: hasMoreRef.current,
            lastUpdatedAt: lastUpdatedAtRef.current,
            offset: offsetRef.current,
            feedsWithMore: { ...feedsWithMoreRef.current },
            cursors: cursorsRef.current && { ...cursorsRef.current },
            filters: activeFiltersRef.current,
            pickupWindow: activePickupWindowRef.current,
        }
    }, [])

    const dropSnapshot = useCallback((tabId: string) => {
        delete cacheRef.current[tabId]
    }, [])

    /**
     * Release a CLOSED tab's live subscriptions. Separate from `dropSnapshot` on purpose.
     *
     * Dropping a cached board and closing a tab look alike and are not: switching DAT on drops every other
     * tab's snapshot so that switching to one re-searches instead of restoring a board missing those rows —
     * and those tabs are still open. Releasing their subscriptions there would be harmless today (a re-search
     * replaces them anyway) but it is not what the caller asked for, and the next caller to drop a snapshot
     * for a tab it means to keep would silently lose that tab's feed.
     */
    const releaseBoard = useCallback(
        (tabId: string) => {
            const boardInstanceId = boardSearchInstanceId(tabId)
            liveSearch.endBoard(boardInstanceId)
            // The lifetime goes with the lanes it described. Left behind, a closed tab's entry would keep
            // growing this record for the session, and reopening a tab under the same id would inherit a
            // stale expiry whose renewal attempt is already spent.
            setLiveSearchLifetimes((prev) => {
                if (!(boardInstanceId in prev)) return prev
                const next = { ...prev }
                delete next[boardInstanceId]
                return next
            })
        },
        [liveSearch]
    )

    // Thaw a tab's frozen board: swap the loads + cursors back in with no network round-trip. The
    // live prepend, quiet refresh and infinite scroll all read the refs restored here, so they resume exactly
    // where the tab left off. Returns false when the tab was never cached (a first visit → caller searches).
    const restoreSnapshot = useCallback(
        (tabId: string): boolean => {
            const snap = cacheRef.current[tabId]
            if (!snap) return false
            // Abort the outgoing tab's in-flight streams before the board is swapped under them.
            abortRef.current?.abort()
            loadMoreAbortRef.current?.abort()
            mergeAbortRef.current?.abort()
            // Restore the cursors that drive infinite scroll, the live-prepend lane match, and quiet refresh.
            activeFiltersRef.current = snap.filters
            activePickupWindowRef.current = snap.pickupWindow
            offsetRef.current = snap.offset
            feedsWithMoreRef.current = { ...snap.feedsWithMore }
            cursorsRef.current = snap.cursors && { ...snap.cursors }
            loadsRef.current = snap.loads
            // Restore the reactive board state.
            setLoads(snap.loads)
            setHasMore(snap.hasMore)
            setLastUpdatedAt(snap.lastUpdatedAt)
            setError(null)
            setLoading(false)
            setLoadingMore(false)
            // Overlays (bids / operator bids / ratings) are a single board-wide map that the previous tab
            // overwrote for its own loads — refetch them for the restored loads. Best-effort; the controller
            // lives in abortRef so the next search/switch aborts it.
            const controller = new AbortController()
            abortRef.current = controller
            fetchOverlaysFor(snap.loads, controller.signal)
            return true
        },
        [fetchOverlaysFor]
    )

    // Abort any in-flight streams on unmount. No auto-search on mount — the page drives search per tab.
    useEffect(
        () => () => {
            abortRef.current?.abort()
            loadMoreAbortRef.current?.abort()
            mergeAbortRef.current?.abort()
        },
        []
    )

    // Live rows reach this board only through its own live-search subscription (`useBoxTruckLiveSearch`), which the
    // server matches against the search's criteria. The operator broadcast of every new Sylectus posting
    // (`box-truck:loads-created`) is NOT listened to here: it carries no coordinates, so this board could not tell a
    // Texas posting from a California one, and it is delivered to the mobile app, which owns that feed.
    // Keep loadsRef in sync so a load-more append (and the live prepend below) seed their dedup map from
    // the true current set — including any live-arrived loads that aren't part of a fetched page.
    useEffect(() => {
        loadsRef.current = loads
    }, [loads])

    return {
        loads,
        loading,
        error,
        lastUpdatedAt,
        bids,
        operatorBids,
        annotations,
        emailThreads,
        threadsKnownFor,
        setAnnotation,
        search,
        clear,
        refresh,
        loadMore,
        hasMore,
        loadingMore,
        saveSnapshot,
        restoreSnapshot,
        dropSnapshot,
        releaseBoard,
        liveSearchLifetimes,
        forgetGatedSource,
        revealGatedSource,
    }
}
